// R39 — PUTTING A READING IN MY ROOM, AND THE ANSWER THAT WAS NEVER READ.
//
// THE LIVE DEFECT THIS FILE EXISTS TO CLOSE. `handlePromoteToMyRoom` answers **HTTP 200 with
// `{ ok: false }`** when the row is not this member's, or when the database is not configured — the
// same shape the rename route uses, and the shape R18 was written about: a status the server
// DELIBERATELY SENT is an ANSWER, not transport noise. The client read `if (r.ok)`, which is the
// HTTP status and nothing else, so a REFUSED save marked the row `inMyRoom: true` on her screen and
// told her "<title> is in My Room". It was nowhere. She found out on the next reload, if ever.
//
// So the promote lives here, it reads the PAYLOAD, and it returns a boolean the surface must honour.
// W5-2's `saved-books.ts` is the same file one object over; the words live here for the same reason
// they live there — four copies of a member-facing string is how two of them end up disagreeing.
//
// This module imports NOTHING (the entry requirement for `scripts/`-loadable law).

// Held by the gate against the server's own route regex, both directions.
export const promoteToMyRoomPath = (id: string | number) => `/api/rooms/report/${id}/my-room`;

// The full sentence, where there is room for one (the modal's toolbar).
export const myRoomButtonLabel = (saved: boolean) =>
  saved ? "✓ In My Room" : "＋ Save to My Room";

// The shelf CARD has room for a mark, not a sentence, so its words live in the accessible name —
// which is also what a rendered gate can read back. It names the reading, because a shelf of eight
// needs eight distinguishable controls.
export const myRoomAriaLabel = (title: string, saved: boolean) =>
  saved ? `${title} is in My Room` : `Save ${title} to My Room`;

// TRUE only if the server said the row really moved. `ok:false` at 200 is a refusal; so is a 401
// from a session that has gone; so is a fetch that never landed.
export async function saveReadingToMyRoom(id: string | number): Promise<boolean> {
  try {
    const res = await fetch(promoteToMyRoomPath(id), { method: "POST" });
    const data = await res.json().catch(() => ({}));
    return res.ok && data?.ok !== false;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------------------------
// R47 — SAVING A FRESHLY GENERATED READING, AND THE TWO SURFACES THAT NEVER READ THE ANSWER.
//
// R39 (above) closed this shape on the PROMOTE route and named the trap in this file's own header.
// It was live in TWO more places on the SAVE route, and they were found by counting: `saveToRoom`
// was hand-typed **twenty-three times** in App.tsx, once per room, and twenty-one of them read the
// payload. The other two read `res.ok`, which is the HTTP status and nothing else:
//
//   * the BUBBLE reading modal — `if (!res.ok) throw new Error("save failed")`, then
//     "Saved to your room."
//   * the FACE-DOWN TAROT reader (`saveReading`) — `if (!response.ok) throw new Error()`, then
//     `saveState = "saved"`, and the button reads the "saved" word for the rest of the session.
//
// `handleReports` (server/index.js) answers **HTTP 200 with `{ ok: false }`** when `ensureUser`
// comes back empty — "Report storage is not connected yet." So on a database the site could not
// reach, a member who had just paid for a generation was told her reading was on her shelf. It was
// nowhere, and there is no second telling: she finds out on the next reload, if ever.
//
// So the transport lives here ONCE and hands back an ANSWER rather than a status code. Twenty-three
// call sites cannot drift from a law they no longer contain (R31: the crossing stops existing).
//
// The COPY stays at the call site, because this module imports nothing and cannot read the string
// catalog — `saved-books.ts`'s fallback-parameter shape, one object over.

export const saveReadingPath = "/api/astrology/reports";

export type SaveReadingBody = {
  type: string;
  title: string;
  data: unknown;
  genKey?: string | null;
  // SH1: an explicit save defaults to My Room. `false` keeps the row shelf-only.
  myRoom?: boolean;
};

// FOUR answers, and the two in the middle are the ones a status code cannot tell apart.
//   sign-in — the session is gone (401). Her words are not lost; she signs in and taps again.
//   refused — the server DELIBERATELY said no, at 200 or at 4xx/5xx, and usually said why.
//   offline — the request never landed. Nothing was decided; this is transport noise (R18).
export type SaveReadingAnswer =
  | { ok: true }
  | { ok: false; kind: "sign-in" }
  | { ok: false; kind: "refused"; message: string }
  | { ok: false; kind: "offline" };

// The three sentences every room shows around a save, handed in by whoever has the catalog.
export type SaveReadingCopy = { done: string; signIn: string; failed: string };

// `doFetch` is a PARAMETER (R46's locked-door shape) so a gate can drive every answer the route
// can give without a browser, a session or a database.
export async function postReadingToRoom(
  body: SaveReadingBody,
  doFetch: typeof fetch = fetch,
): Promise<SaveReadingAnswer> {
  try {
    const res = await doFetch(saveReadingPath, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.status === 401) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "sign-in" }; }
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.ok === false) {
      return { ok: false, kind: "refused", message: typeof data?.message === "string" ? data.message : "" };
    }
    return { ok: true };
  } catch {
    return { ok: false, kind: "offline" };
  }
}

// The server's own sentence wins for a refusal — it is the only one that can say WHY. It is never
// used for a 401: the session is gone, and "Sign in" is the only useful thing to say about that.
export function saveReadingNote(answer: SaveReadingAnswer, copy: SaveReadingCopy): string {
  if (answer.ok) return copy.done;
  if (answer.kind === "sign-in") return copy.signIn;
  if (answer.kind === "refused" && answer.message) return answer.message;
  return copy.failed;
}

// The save chip's state, derived from the answer rather than re-typed per room. A refusal is an
// ERROR, never "saved" — that is the whole finding, in one expression.
export function saveReadingState(answer: SaveReadingAnswer): "saved" | "error" {
  return answer.ok ? "saved" : "error";
}
